import { useEffect, useMemo, useRef, useState } from "react";
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";

const SIZE_CLASSES = {
  xs: {
    chip: "pl-1.5 pr-1 py-0.5 text-[10px]",
    btn: "px-1.5 py-0.5 text-[10px]",
    input: "px-2 py-0.5 text-xs",
    icon: "h-2.5 w-2.5",
  },
  sm: {
    chip: "pl-2 pr-1.5 py-0.5 text-xs",
    btn: "px-2 py-1 text-xs",
    input: "px-2 py-1 text-xs",
    icon: "h-3 w-3",
  },
  md: {
    chip: "pl-2 pr-1.5 py-1 text-sm",
    btn: "px-2.5 py-1 text-xs",
    input: "px-3 py-1.5 text-sm",
    icon: "h-3.5 w-3.5",
  },
};

/** Inline custom-tag editor with typeahead against the org-wide tag set. */
export default function VendorTagsEditor({
  tags = [],
  allKnownTags = [],
  onAddTag,
  onRemoveTag,
  size = "sm",
  className = "",
  disabled = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const sizeClasses = SIZE_CLASSES[size] || SIZE_CLASSES.sm;

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const suggestions = useMemo(() => {
    const q = draft.trim().toLowerCase();
    if (!q) {
      return allKnownTags.filter(
        (t) => !tags.some((existing) => existing.toLowerCase() === t.toLowerCase())
      );
    }
    return allKnownTags.filter(
      (t) =>
        t.toLowerCase().includes(q) &&
        !tags.some((existing) => existing.toLowerCase() === t.toLowerCase())
    );
  }, [allKnownTags, draft, tags]);

  const showSuggestions = editing && (suggestions.length > 0 || draft.trim());

  const commitTag = (value) => {
    const v = String(value || "").trim();
    if (!v || disabled) return;
    onAddTag?.(v);
    setDraft("");
    setHighlightIndex(-1);
    setEditing(false);
  };

  const cancel = () => {
    setDraft("");
    setHighlightIndex(-1);
    setEditing(false);
  };

  return (
    <div
      className={`relative flex flex-wrap items-center gap-1 ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      {tags.map((tag) => (
        <span
          key={tag}
          className={`inline-flex items-center gap-1 rounded-md bg-gray-100 text-gray-700 font-medium ${sizeClasses.chip}`}
        >
          {tag}
          {!disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveTag?.(tag);
              }}
              className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-gray-200"
              aria-label={`Remove tag ${tag}`}
            >
              <XMarkIcon className={`${sizeClasses.icon} text-gray-500`} />
            </button>
          )}
        </span>
      ))}

      {editing ? (
        <div className="relative min-w-[10rem]">
          <input
            ref={inputRef}
            type="text"
            placeholder="Tag name"
            value={draft}
            disabled={disabled}
            onChange={(e) => {
              setDraft(e.target.value);
              setHighlightIndex(-1);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlightIndex((i) =>
                  Math.min(i + 1, Math.max(suggestions.length - 1, 0))
                );
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightIndex((i) => Math.max(i - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                if (highlightIndex >= 0 && suggestions[highlightIndex]) {
                  commitTag(suggestions[highlightIndex]);
                } else {
                  commitTag(draft);
                }
              } else if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                cancel();
              }
            }}
            onBlur={() => {
              window.setTimeout(() => {
                if (draft.trim()) commitTag(draft);
                else cancel();
              }, 150);
            }}
            className={`w-full rounded-md bg-white text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 ${sizeClasses.input}`}
          />
          {showSuggestions && (
            <ul
              ref={listRef}
              className="absolute left-0 top-full z-20 mt-1 max-h-40 w-full overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
            >
              {suggestions.map((suggestion, idx) => (
                <li key={suggestion}>
                  <button
                    type="button"
                    className={`block w-full px-3 py-1.5 text-left text-sm ${
                      idx === highlightIndex
                        ? "bg-indigo-50 text-indigo-700"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      commitTag(suggestion);
                    }}
                  >
                    {suggestion}
                  </button>
                </li>
              ))}
              {draft.trim() &&
                !suggestions.some(
                  (s) => s.toLowerCase() === draft.trim().toLowerCase()
                ) && (
                  <li>
                    <button
                      type="button"
                      className="block w-full px-3 py-1.5 text-left text-sm text-gray-500 hover:bg-gray-50"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        commitTag(draft);
                      }}
                    >
                      Add &ldquo;{draft.trim()}&rdquo;
                    </button>
                  </li>
                )}
            </ul>
          )}
        </div>
      ) : (
        !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setEditing(true);
            }}
            className={`inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 font-medium ${sizeClasses.btn}`}
          >
            <PlusIcon className={sizeClasses.icon} aria-hidden="true" />
            Add
          </button>
        )
      )}
    </div>
  );
}
